import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Edit, Save, X, User, Phone, Mail, MapPin, Calendar, FileText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const PatientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [patientData, setPatientData] = useState({
    id: id,
    name: 'John Doe',
    email: 'john.doe@email.com',
    phone: '(555) 123-4567',
    dateOfBirth: '1985-03-15',
    address: '123 Main St, Springfield, IL 62701',
    insuranceProvider: 'Blue Cross Blue Shield',
    insuranceId: 'BC123456789',
    emergencyContact: 'Jane Doe',
    emergencyPhone: '(555) 987-6543',
    medicalHistory: 'No known allergies. Previous wisdom teeth extraction in 2010.',
    status: 'active'
  });

  useEffect(() => {
    // HIPAA Audit Log - Log patient data access
    logDataAccess();
  }, [id]);

  const logDataAccess = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hipaa/audit-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          action: 'view',
          resource_type: 'patient',
          resource_id: id,
          details: { accessed_from: 'patient_detail_page' }
        })
      });
    } catch (error) {
      console.error('Error logging access:', error);
    }
  };

  const referrals = [
    { id: 1, date: '2025-11-08', procedure: 'Wisdom Teeth Extraction', status: 'scheduled', doctor: 'Dr. Sarah Johnson' },
    { id: 2, date: '2025-10-15', procedure: 'Dental Implant Consultation', status: 'completed', doctor: 'Dr. Michael Chen' },
    { id: 3, date: '2025-09-22', procedure: 'Root Canal', status: 'completed', doctor: 'Dr. Gerard Nikodem' }
  ];

  const handleSave = async () => {
    // In production, call API to update patient
    try {
      // HIPAA Audit Log - Log patient data modification
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hipaa/audit-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          action: 'update',
          resource_type: 'patient',
          resource_id: id,
          details: { fields_updated: Object.keys(patientData) }
        })
      });
      
      console.log('Saving patient data:', patientData);
      alert('Patient information updated successfully!');
      setEditMode(false);
    } catch (error) {
      console.error('Error saving patient data:', error);
      alert('Error updating patient information');
    }
  };

  const handleCancel = () => {
    // Reset to original data
    setEditMode(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800 border-green-200',
      inactive: 'bg-gray-100 text-gray-800 border-gray-200',
      scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => navigate('/patients')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Patients
        </Button>
        {!editMode ? (
          <Button
            className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
            onClick={() => setEditMode(true)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Patient
          </Button>
        ) : (
          <div className="flex space-x-2">
            <Button
              className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
              onClick={handleSave}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Patient Information */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <User className="h-6 w-6 mr-2 text-[#D4AF37]" />
                  Patient Information
                </CardTitle>
                <Badge className={getStatusColor(patientData.status)}>
                  {patientData.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name</label>
                  {editMode ? (
                    <Input
                      value={patientData.name}
                      onChange={(e) => setPatientData({...patientData, name: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-700">{patientData.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Date of Birth</label>
                  {editMode ? (
                    <Input
                      type="date"
                      value={patientData.dateOfBirth}
                      onChange={(e) => setPatientData({...patientData, dateOfBirth: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-700">{new Date(patientData.dateOfBirth).toLocaleDateString()}</p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center">
                    <Mail className="h-4 w-4 mr-1" />
                    Email
                  </label>
                  {editMode ? (
                    <Input
                      type="email"
                      value={patientData.email}
                      onChange={(e) => setPatientData({...patientData, email: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-700">{patientData.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center">
                    <Phone className="h-4 w-4 mr-1" />
                    Phone
                  </label>
                  {editMode ? (
                    <Input
                      value={patientData.phone}
                      onChange={(e) => setPatientData({...patientData, phone: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-700">{patientData.phone}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Address
                </label>
                {editMode ? (
                  <Input
                    value={patientData.address}
                    onChange={(e) => setPatientData({...patientData, address: e.target.value})}
                  />
                ) : (
                  <p className="text-gray-700">{patientData.address}</p>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <label className="block text-sm font-medium mb-2">Insurance Provider</label>
                  {editMode ? (
                    <Input
                      value={patientData.insuranceProvider}
                      onChange={(e) => setPatientData({...patientData, insuranceProvider: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-700">{patientData.insuranceProvider}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Insurance ID</label>
                  {editMode ? (
                    <Input
                      value={patientData.insuranceId}
                      onChange={(e) => setPatientData({...patientData, insuranceId: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-700">{patientData.insuranceId}</p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <label className="block text-sm font-medium mb-2">Emergency Contact</label>
                  {editMode ? (
                    <Input
                      value={patientData.emergencyContact}
                      onChange={(e) => setPatientData({...patientData, emergencyContact: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-700">{patientData.emergencyContact}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Emergency Phone</label>
                  {editMode ? (
                    <Input
                      value={patientData.emergencyPhone}
                      onChange={(e) => setPatientData({...patientData, emergencyPhone: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-700">{patientData.emergencyPhone}</p>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t">
                <label className="block text-sm font-medium mb-2">Medical History</label>
                {editMode ? (
                  <Textarea
                    value={patientData.medicalHistory}
                    onChange={(e) => setPatientData({...patientData, medicalHistory: e.target.value})}
                    rows={4}
                  />
                ) : (
                  <p className="text-gray-700 whitespace-pre-wrap">{patientData.medicalHistory}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Referral History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-6 w-6 mr-2 text-[#D4AF37]" />
                Referral History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {referrals.map((referral) => (
                  <div key={referral.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <p className="font-semibold text-[#0A1628]">{referral.procedure}</p>
                        <Badge className={getStatusColor(referral.status)}>
                          {referral.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600">Referred by {referral.doctor}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        <Calendar className="h-3 w-3 inline mr-1" />
                        {new Date(referral.date).toLocaleDateString()}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => navigate(`/referrals/${referral.id}`)}>
                      View
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Referrals</span>
                <span className="font-semibold text-[#0A1628]">{referrals.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Completed</span>
                <span className="font-semibold text-green-600">
                  {referrals.filter(r => r.status === 'completed').length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Upcoming</span>
                <span className="font-semibold text-blue-600">
                  {referrals.filter(r => r.status === 'scheduled').length}
                </span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t">
                <span className="text-sm text-gray-600">Last Visit</span>
                <span className="font-semibold text-[#D4AF37]">
                  {referrals.length > 0 ? new Date(referrals[0].date).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button className="w-full" variant="outline" onClick={() => navigate('/referrals/new')}>
                Create New Referral
              </Button>
              <Button className="w-full" variant="outline">
                Schedule Appointment
              </Button>
              <Button className="w-full" variant="outline">
                Send Message
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PatientDetail;